import React, { useCallback, useEffect } from 'react';
import { default as CapabilitySetComponent } from '../components/CapabilitySet';

import { connect } from 'react-redux';
import { useParams } from 'react-router-dom';
import { denormalize } from 'normalizr';
import {
  loadCapabilitySet,
  updateCapabilitySet,
  createCapability,
  updateCapability,
  updateCapabilityPositions,
} from 'js/actions/capability-actions';
import { useNotify } from 'js/hooks/useNotify';
import { SCHEMAS } from 'js/api/schemas';
import { useDispatch } from 'react-redux';
function CapabilitySet({
  capabilitySet,
  loadCapabilitySet,
  updateCapabilitySet,
  createCapability,
  updateCapability,
  updateCapabilityPositions,
}) {
  const { capabilitySetId } = useParams();
  const notify = useNotify();
  const dispatch = useDispatch();

  const refreshCapabilitySet = useCallback(
    () => loadCapabilitySet(capabilitySetId),
    [loadCapabilitySet, capabilitySetId]
  );

  useEffect(() => {
    refreshCapabilitySet();
  }, [refreshCapabilitySet]);

  const handleUpdate = useCallback(
    async (values) => {
      return updateCapabilitySet(capabilitySetId, values)
        .then(() => {
          notify({
            type: 'success',
            message: 'Capability set updated successfully',
          });
          return refreshCapabilitySet();
        })
        .catch((error) => {
          notify({
            type: 'danger',
            message: 'Failed to update capability set',
          });
          throw error;
        });
    },
    [updateCapabilitySet, capabilitySetId, notify, refreshCapabilitySet]
  );

  const handleAddCapability = useCallback(
    async (props) => {
      // console.log('parent', parent, props);
      // return;

      return await createCapability(capabilitySetId, props).then(() =>
        refreshCapabilitySet()
      );
    },
    [capabilitySetId, createCapability, refreshCapabilitySet]
  );

  const handleEditCapability = useCallback(
    async (values) =>
      updateCapability(capabilitySetId, values.id, values).then(() =>
        refreshCapabilitySet()
      ),
    [capabilitySetId, refreshCapabilitySet, updateCapability]
  );

  const moveCapability = useCallback(
    async ({ id, old_parent_id, new_parent_id, positions }) => {
      const { [id]: position, ...siblingPositions } = positions;

      try {
        if (old_parent_id !== new_parent_id) {
          dispatch({
            type: 'REPARENT_CAPABILITY',
            capability_set_id: capabilitySetId,
            id,
            old_parent_id,
            new_parent_id,
            position,
          });
          await updateCapability(capabilitySetId, id, {
            id,
            parent_id: new_parent_id,
            position,
          });
        } else {
          siblingPositions[id] = position;
        }

        if (Object.keys(siblingPositions).length > 0) {
          await updateCapabilityPositions(capabilitySetId, siblingPositions);
        }
      } catch (error) {
        notify({ type: 'danger', message: 'Failed to move capability' });
      }

      return refreshCapabilitySet();
    },
    [
      capabilitySetId,
      refreshCapabilitySet,
      updateCapability,
      updateCapabilityPositions,
      dispatch,
      notify,
    ]
  );

  const repositionCapabilities = useCallback(
    async (positions) => {
      try {
        await updateCapabilityPositions(capabilitySetId, positions);
      } catch (error) {
        notify({ type: 'danger', message: 'Failed to sort capabilities' });
      }

      return refreshCapabilitySet();
    },
    [capabilitySetId, updateCapabilityPositions, refreshCapabilitySet, notify]
  );

  return (
    <CapabilitySetComponent
      capabilitySet={capabilitySet}
      onUpdate={handleUpdate}
      onAddCapability={handleAddCapability}
      onEditCapability={handleEditCapability}
      onMoveCapability={moveCapability}
      onRepositionCapabilities={repositionCapabilities}
    />
  );
}

export default connect(
  (state, { match }) => ({
    capabilitySet: denormalize(
      state.entities.capabilitySets[match.params.capabilitySetId],
      SCHEMAS.CAPABILITY_SET,
      state.entities
    ),
  }),
  {
    loadCapabilitySet,
    updateCapabilitySet,
    createCapability,
    updateCapability,
    updateCapabilityPositions,
  }
)(CapabilitySet);
